import { SVG } from 'components/Asset/SVG';
import {
  IdSpan,
  OperationalStatusText,
} from 'components/AgGrid/CellRenderer/ListView.styled';
import { theme } from 'mui.theme';
import { type Asset } from 'types/asset.types';
import { StyledIcon } from 'components/AgGrid/CellRenderer/CellRenderer.styled';
import { ASSET_OPERATIONAL_STATUS } from 'utils/enums';
import { resolveAssetTypeDisplay } from 'utils/helpers/assets';
import { checkDeviceStatus } from 'store/utils';
import { type Device as DeviceType } from 'types/device.types';
import { handleAssetStatus } from '../helpers/common';
import { notificationLevel } from 'views/FleetOverview/helpers/asset.helpers';
import { capitalizeFirstLetter } from 'utils/helpers/string.manipulation';
import { type RuleNotifications } from 'types/notification.types';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { useTranslation } from 'react-i18next';
const { svgIcons } = theme.palette;

export interface AssetListRendererProp {
  value: string;
  data: Asset;
  device: DeviceType & Record<string, number>;
  handleIdClick: (
    asset_id: string,
    assetVin?: string,
    make?: string,
    model?: string,
    bumperNumber?: string
  ) => void;
  handleAlertClick: (
    notifications: RuleNotifications[] | undefined,
    bumperNumber: string
  ) => void;
  handleDeleteAsset: (asset_id: string) => void;
  handleOperatorClick: (asset_id: string) => void;
  handleTagClick: (asset_id: string) => void;
  handleDeviceClick: (asset_id: string) => void;
  handleNoDeviceClick: (arg0: string) => void;
  type: string;
  colDef: any;
  latestTagData: any;
  assetsWithLiveData: any;
}

export interface AlertListRendererProp {
  value: string;
  data: Asset;
  handleAlertClick: (
    notifications: RuleNotifications[] | undefined,
    bumperNumber: string
  ) => void;
  type: string;
  colDef: any;
}

export const DeleteAsset = ({
  data: { assetId },
  handleDeleteAsset,
}: AssetListRendererProp) => {
  return (
    <span
      onClick={() => {
        handleDeleteAsset(assetId);
      }}
    >
      <SVG
        name="deleteIcon"
        style={{
          ...svgIcons?.primary,
        }}
      />
    </span>
  );
};

export const AssetId = ({
  handleIdClick,
  data: { assetVin, assetId },
}: Pick<AssetListRendererProp, 'handleIdClick' | 'data'>) => {
  return (
    <IdSpan
      onClick={() => {
        handleIdClick(assetId);
      }}
    >
      {assetVin}
    </IdSpan>
  );
};

export const TPMS = ({ data: { tpmsEnabled } }: AssetListRendererProp) => {
  return (
    <span
      style={{
        color: tpmsEnabled ? '#749A4A' : '#A03434',
      }}
    >
      {tpmsEnabled ? 'Yes' : 'No'}
    </span>
  );
};

export const MissingDevice = ({
  handleDeviceClick,
}: {
  handleDeviceClick: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <>
      <StyledIcon>
        <SVG
          name={'offlineIcon'}
          style={{
            ...svgIcons.error,
          }}
        />
      </StyledIcon>
      <IdSpan
        onClick={() => {
          handleDeviceClick();
        }}
      >
        {t('no_device')}
      </IdSpan>
    </>
  );
};

export const DeviceStatus = ({
  handleDeviceClick,
  handleNoDeviceClick,
  data: { device, assetId, liveTagData },
}: Pick<
  AssetListRendererProp,
  'data' | 'handleDeviceClick' | 'handleNoDeviceClick'
>) => {
  const { t } = useTranslation();
  const [isMissing, isActive, isXYFound] = checkDeviceStatus(device);

  if (isMissing) {
    return (
      <MissingDevice
        handleDeviceClick={() => {
          handleNoDeviceClick(assetId);
        }}
      />
    );
  }

  if (!isXYFound) {
    return (
      <>
        <StyledIcon>
          <SVG
            name={'offlineIcon'}
            style={{
              ...svgIcons.error,
            }}
          />
        </StyledIcon>
        <IdSpan
          onClick={() => {
            handleDeviceClick(assetId);
          }}
        >
          {t('missing_x_y')}
        </IdSpan>
      </>
    );
  }
  return (
    <span>
      <StyledIcon>
        <SVG
          name={
            device.deviceStatus === 'Online'
              ? 'greenIcon'
              : device.deviceStatus === 'Recently Online'
              ? 'activeIcon'
              : device.deviceStatus === 'Offline'
              ? 'offlineIcon'
              : 'redIcon'
          }
          style={{
            fill: device.deviceStatus === 'Online' ? 'green' : 'red',
          }}
        />
      </StyledIcon>
      {device.deviceStatus}
    </span>
  );
};

export const Device = ({
  handleDeviceClick,
  handleIdClick,
  data: { assetId, device, engineHoursOffset, odometerOffset },
}: AssetListRendererProp) => {
  const { t } = useTranslation();
  /*
    A device is available to be associated if EITHER
    the engineHoursOffset or odometerOffset is greater than 0
  */

  const hasEssentialData = engineHoursOffset > 0 || odometerOffset > 0;

  if (!hasEssentialData) {
    return (
      <>
        <StyledIcon>
          <SVG
            name={'offlineIcon'}
            style={{
              ...svgIcons.error,
            }}
          />
        </StyledIcon>
        <IdSpan
          onClick={() => {
            // should open asset modal
            handleIdClick(assetId);
          }}
        >
          {t('missing_data')}
        </IdSpan>
      </>
    );
  }

  if (hasEssentialData && device?.deviceId) {
    return (
      <IdSpan
        onClick={() => {
          // should open device modal
          handleDeviceClick(assetId);
        }}
      >
        {device.deviceId}
      </IdSpan>
    );
  }
};

export const AssetType = ({
  data: { assetType },
}: Pick<AssetListRendererProp, 'data'>) => {
  return <>{resolveAssetTypeDisplay(assetType)}</>;
};

export const OperationalStatus = ({
  data: { isActive },
}: Pick<AssetListRendererProp, 'data'>) => {
  const operationalStatus = handleAssetStatus(isActive);
  const assetOperationalStatus = (ASSET_OPERATIONAL_STATUS as any)[
    operationalStatus
  ];
  if (!assetOperationalStatus) return null;
  const isOperational =
    assetOperationalStatus === ASSET_OPERATIONAL_STATUS.operational;
  return (
    <>
      <OperationalStatusText isOperational={isOperational}>
        {assetOperationalStatus}
      </OperationalStatusText>
    </>
  );
};

export const AlertStatus = ({
  data: { notifications },
}: Pick<AssetListRendererProp, 'data'>) => {
  const { color, warning } = notificationLevel(notifications);
  return (
    <IdSpan style={{ color }}>
      <>{warning}</>
    </IdSpan>
  );
};

export const DeviceIngestDate = ({
  data: { device },
}: Pick<AssetListRendererProp, 'data'>) => {
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();
  return (
    <span>
      {convertUtcToDisplayTime(
        device.ingestDate,
        preferredTimeDisplayMode,
        timezone,
        'DD/MM/YYYY HH:mm'
      )}
    </span>
  );
};

export const FuelSource = ({
  data: { fuelSource },
}: Pick<AssetListRendererProp, 'data'>) => {
  return <span>{capitalizeFirstLetter(fuelSource)}</span>;
};
